import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ItemCard from "./ItemCard";

const GalleryView = ({ items, user }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const itemIds = items.map((item) => item.id);

  // Filter states
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [statusDropdownRef] = useState(useRef(null));
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
  const [isDesignerSectionOpen, setIsDesignerSectionOpen] = useState(true);
  const [isStatusSectionOpen, setIsStatusSectionOpen] = useState(false);

  const nonSoldItems = items.filter((item) => item.status !== "Sold");
  const soldOutItems = items.filter((item) => item.status === "Sold");

  const designers = Array.from(
    new Set(nonSoldItems.map((item) => item.designer))
  ).sort();

  const designerCounts = designers.map((designer) => ({
    name: designer,
    count: items.filter((item) => item.designer === designer).length,
  }));

  const sortedDesigners = designerCounts
    .sort((a, b) => b.count - a.count)
    .map((item) => item.name);

  const selectedDesigner = (() => {
    const designerParam = searchParams.get("designer");
    if (!designerParam) return "All";
    // Find the designer whose slug matches the param
    const match = designers.find(
      (d) => d.toLowerCase().replace(/ /g, "-") === designerParam
    );
    return match || "All";
  })();

  const statusFilter = searchParams.get("status") || "All";

  const designerFilteredItems =
    selectedDesigner === "All"
      ? nonSoldItems
      : nonSoldItems.filter((item) => item.designer === selectedDesigner);

  const finalAvailableItems = designerFilteredItems.filter((item) => {
    if (statusFilter === "All") return true;
    if (statusFilter === "available")
      return item.status !== "On Hold" && item.status !== "Sold";
    if (statusFilter === "on hold") return item.status === "On Hold";
    return true;
  });

  const availableItemsSorted = [...finalAvailableItems].sort(
    (a, b) => new Date(b._createdAt) - new Date(a._createdAt)
  );

  const [condensedView, setCondensedView] = useState(() => {
    const stored = localStorage.getItem("condensedView");
    if (!stored || stored === "undefined") return false;
    try {
      return JSON.parse(stored);
    } catch {
      return false;
    }
  });

  const handleInquire = (formData, item) => {
    console.log("Inquiry received:", { formData, item });
  };
  const handleMakeOffer = (formData, item) => {
    console.log("Offer received:", { formData, item });
  };

  const handleToggleView = (isCondensed) => {
    setCondensedView(isCondensed);
    localStorage.setItem("condensedView", JSON.stringify(isCondensed));
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setIsStatusDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [statusDropdownRef]);

  const handleDesignerChange = (designer) => {
    console.log(designer);
    const newSearchParams = new URLSearchParams(searchParams);
    if (designer === "All") {
      newSearchParams.delete("designer");
    } else {
      newSearchParams.set(
        "designer",
        designer.toLowerCase().replace(/ /g, "-")
      );
    }
    setSearchParams(newSearchParams);
  };

  const handleStatusChange = (newStatus) => {
    setIsStatusDropdownOpen(false);
    const newSearchParams = new URLSearchParams(searchParams);
    if (newStatus === "All") {
      newSearchParams.delete("status");
    } else {
      newSearchParams.set("status", newStatus.toLowerCase());
    }
    setSearchParams(newSearchParams);
  };

  const isMobile = window.innerWidth < 768;
  const [visibleCount, setVisibleCount] = useState(isMobile ? 4 : 8);

  useEffect(() => {
    const handleScroll = () => {
      const threshold = isMobile ? 1900 : 1700; // Mobile: 300px, Desktop: 1200px

      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - threshold
      ) {
        setVisibleCount((prev) => prev + (isMobile ? 2 : 4));
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  useEffect(() => {
    // Update document title
    if (selectedDesigner && selectedDesigner !== "All") {
      document.title = `${selectedDesigner} | Titled Works`;
    } else {
      document.title = "Titled Works | Curator Access";
    }
  }, [selectedDesigner]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6 flex justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            {/* Filter Button */}
            <button
              onClick={() => setIsFiltersDrawerOpen(true)}
              className={`flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg transition-colors ${
                selectedDesigner !== "All" || statusFilter !== "All"
                  ? "bg-black text-white hover:bg-gray-900"
                  : "bg-white text-black hover:bg-black hover:text-white"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              <span className="text-sm font-medium">Filter</span>
            </button>
          </div>

          {/* Item count */}
          <div className="text-sm text-gray-400">
            {availableItemsSorted.length} items
          </div>
        </div>

        {/* Filters Drawer */}
        {isFiltersDrawerOpen && (
          <div className="fixed inset-0 z-50 pointer-events-none">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-30 transition-opacity duration-300 pointer-events-auto"
              onClick={() => setIsFiltersDrawerOpen(false)}
            />
            {/* Drawer */}
            <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 pointer-events-auto">
              <div className="px-6 pt-6 pb-4 h-full flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-3xl">Filters</h2>
                  <button
                    onClick={() => setIsFiltersDrawerOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto space-y-4 flex flex-col">
                  {/* Designer Filter */}
                  <div
                    className={
                      isDesignerSectionOpen ? "flex-1 min-h-0" : "flex-shrink-0"
                    }
                  >
                    <button
                      onClick={() =>
                        setIsDesignerSectionOpen(!isDesignerSectionOpen)
                      }
                      className="w-full py-3 text-left flex items-center justify-between hover:bg-gray-50"
                    >
                      <h3 className="text-sm font-medium text-gray-700 text-kin">
                        Designer
                      </h3>
                      <svg
                        className={`w-4 h-4 transform transition-transform ${isDesignerSectionOpen ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {isDesignerSectionOpen && (
                      <div className="pb-4 h-full overflow-y-auto">
                        <div className="space-y-2">
                          <button
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selectedDesigner === "All" ? "bg-black text-white" : "bg-gray-100 text-gray-800 hover:bg-gray-200"}`}
                            onClick={() => handleDesignerChange("All")}
                          >
                            All Designers
                          </button>
                          {sortedDesigners.map((designer) => (
                            <button
                              key={designer}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selectedDesigner === designer ? "bg-black text-white" : "bg-gray-100 text-gray-800 hover:bg-gray-200"}`}
                              onClick={() => handleDesignerChange(designer)}
                            >
                              {designer}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Status Filter */}
                  <div className="flex-shrink-0 bg-white z-10">
                    <button
                      onClick={() =>
                        setIsStatusSectionOpen(!isStatusSectionOpen)
                      }
                      className="w-full py-3 text-left flex items-center justify-between pt-4"
                    >
                      <h3 className="text-sm font-medium text-gray-700 text-kin">
                        Status
                      </h3>
                      <svg
                        className={`w-4 h-4 transform transition-transform ${isStatusSectionOpen ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {isStatusSectionOpen && (
                      <div className="pb-4 max-h-48 overflow-y-auto">
                        <div className="space-y-2">
                          <button
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${statusFilter === "All" ? "bg-black text-white" : "bg-gray-100 text-gray-800 hover:bg-gray-200"}`}
                            onClick={() => handleStatusChange("All")}
                          >
                            All Statuses
                          </button>
                          <button
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${statusFilter === "Available" ? "bg-black text-white" : "bg-gray-100 text-gray-800 hover:bg-gray-200"}`}
                            onClick={() => handleStatusChange("Available")}
                          >
                            Available
                          </button>
                          <button
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${statusFilter === "On Hold" ? "bg-black text-white" : "bg-gray-100 text-gray-800 hover:bg-gray-200"}`}
                            onClick={() => handleStatusChange("On Hold")}
                          >
                            On Hold
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Active Filters Summary */}
                  {(selectedDesigner !== "All" || statusFilter !== "All") && (
                    <div className="flex-shrink-0">
                      <div className="py-3">
                        <h3 className="text-sm font-medium text-gray-700 mb-3 text-kin">
                          Active Filters
                        </h3>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {selectedDesigner !== "All" && (
                            <div className="flex items-center justify-between bg-gray-100 px-3 py-2 rounded-lg">
                              <span className="text-sm">
                                Designer: {selectedDesigner}
                              </span>
                              <button
                                onClick={() => handleDesignerChange("All")}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          )}
                          {statusFilter !== "All" && (
                            <div className="flex items-center justify-between bg-gray-100 px-3 py-2 rounded-lg">
                              <span className="text-sm">
                                Status:{" "}
                                {statusFilter
                                  .split(" ")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1)
                                  )
                                  .join(" ")}
                              </span>
                              <button
                                onClick={() => handleStatusChange("All")}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Fixed Apply Button */}
                <div className="mt-4">
                  <button
                    onClick={() => setIsFiltersDrawerOpen(false)}
                    className="w-full bg-black text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {availableItemsSorted.length === 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              {selectedDesigner !== "All" || statusFilter !== "All" ? (
                <>
                  <h3 className="mt-2 text-4xl font-medium text-gray-900">
                    No items match your filters
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Try clearing some filters to see more results.
                  </p>
                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={() => setSearchParams({})}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                    >
                      Clear All Filters
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="mt-2 text-4xl font-medium text-gray-900">
                    No items available
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Check back later for new additions to the archive.
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Gallery Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 md:pt-6 pb-8">
          {/* Available Items */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6 items-stretch">
            {availableItemsSorted.slice(0, visibleCount).map((item) => (
              <ItemCard
                key={item._id}
                item={item}
                onInquire={handleInquire}
                onMakeOffer={handleMakeOffer}
                onCardClick={() => navigate(`/item/${item._id}`)}
                condensedView={condensedView}
              />
            ))}
          </div>

          {/* Sold Out Section */}
          {soldOutItems.length > 0 && (
            <>
              <h2 className="mt-12 mb-4 text-3xl text-gray-700">Sold</h2>
              <div className="grid grid-cols-4 lg:grid-cols-8 gap-3 md:gap-6">
                {soldOutItems.map((item) => (
                  <ItemCard
                    key={item._id}
                    item={item}
                    onInquire={handleInquire}
                    soldOut={true}
                    onMakeOffer={handleMakeOffer}
                    onCardClick={() => navigate(`/item/${item._id}`)}
                    condensedView={condensedView}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GalleryView;
